import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, MessageCircle, UserPlus, Sparkles, Inbox } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { isStaffRole } from "@/components/RoleBadge";
import { timeAgo } from "@/lib/format";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "@/lib/api";

const ICONS = {
  new_comment: MessageCircle,
  new_user: UserPlus,
  new_contribution: Sparkles,
};

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const staff = isStaffRole(user?.role);

  const load = useCallback(async () => {
    try {
      const data = await getNotifications();
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silent — bell just stays quiet */
    }
  }, []);

  useEffect(() => {
    if (!staff) return undefined;
    load();
    timerRef.current = setInterval(load, 45000);
    return () => clearInterval(timerRef.current);
  }, [staff, load]);

  if (!staff) return null;

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await markNotificationRead(n.id); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-button"
          aria-label="Notifications"
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-gold-light/40 hover:text-foreground"
        >
          <Bell className="h-4.5 w-4.5" />
          {unread > 0 ? (
            <span
              data-testid="notification-unread-badge"
              className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-[#05070B]"
              style={{ backgroundColor: "#D8B66A" }}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="notification-panel"
        className="w-80 border-[rgba(216,182,106,0.2)] bg-[rgba(10,20,34,0.92)] p-0 backdrop-blur-xl"
      >
        <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Bell className="h-4 w-4 text-gold-light" /> Notifications
          </div>
          {unread > 0 ? (
            <button
              onClick={markAll}
              data-testid="notification-mark-all-read"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-gold-light"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          ) : null}
        </div>

        <div
          data-testid="notification-scroll"
          className="max-h-96 overflow-y-auto overscroll-contain"
        >
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="notification-empty">
              <Inbox className="mx-auto h-6 w-6 text-muted-foreground/50" />
              <p className="mt-2 text-sm text-muted-foreground">You&apos;re all caught up.</p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => openItem(n)}
                      data-testid={`notification-item-${n.id}`}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.03] ${n.read ? "opacity-70" : ""}`}
                    >
                      <span
                        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: "rgba(216,182,106,0.12)", color: "#D8B66A" }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-[13px] font-medium text-foreground/90">{n.title}</span>
                          {!n.read ? <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "#D8B66A" }} /> : null}
                        </div>
                        {n.body ? <p className="mt-0.5 line-clamp-2 text-[12px] leading-5 text-muted-foreground">{n.body}</p> : null}
                        <span className="mt-1 block font-mono text-[10px] text-muted-foreground/60">{timeAgo(n.created_at)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
